/* eslint-disable max-lines */
import type { Object3D } from 'three';
import { Box3, Group, Matrix4, Plane, Quaternion, Raycaster, Vector2, Vector3 } from 'three';

import { APPLIANCE_DRAG } from './constants';
import { VENT_CUTOUT_TYPE } from '../cutout-manager/helpers/vent-proxy.helpers';
import { applyEmissiveTint, EmissiveTintBackup, restoreEmissiveTint, setEmissiveTintIntensity } from '../../../three-helpers/tint';
import { buildPreviewPlanInputsCfn, choosePlaneCfn, decideSwapTargetCfn, DecisionGroup, describeSlotCfn, fineTuneRoomCfn, FineTuneRoom, hypotheticalWidthsCfn } from './functions/drag-decision.cfn';
import { createDimensionOverlayCfn, DimensionOverlay, disposeDimensionOverlayCfn, placeDimensionOverlayCfn } from './functions/dimension-overlay.cfn';
import {
	createDropIndicatorCfn,
	defaultDropIndicatorCfn,
	disposeDropIndicatorCfn,
	DROP_INDICATOR_TYPE,
	DropIndicator,
	placeDropIndicatorCfn,
	setDropIndicatorContentCfn
} from './functions/drop-indicator.cfn';
import type {
	ApplianceDragManagerConfig,
	ApplianceDragOptions,
	ApplianceDragProvider,
	ApplianceDragRefusedReason,
	ApplianceDragState,
	ApplianceDropAssessment,
	ApplianceDropRequest,
	DragGroupModel,
	DragIslandModel,
	DragModel
} from './types';

// The pre-cutout clones are solid and stay in the island group for the scene's lifetime; three
// raycasts them even though they are invisible, and the walk below gives up on the first non-appliance hit.
const NON_HIT_TYPES = new Set([
	'appliance-cutout',
	'appliance-dissolving',
	'countertop-no-cutout',
	'debug',
	'panel-no-cutout',
	'rotation-gizmo',
	'selection-label',
	'validation-marker',
	'validation-markers',
	VENT_CUTOUT_TYPE,
	DROP_INDICATOR_TYPE
]);

interface ApplianceHit {
	applianceId: string;
	object: Object3D;
	islandGroup: Object3D;
	islandId: string;
}

interface ArmedPress {
	pointerId: number;
	pointerType: string;
	startX: number;
	startY: number;
	hit: ApplianceHit;
	timer: ReturnType<typeof setTimeout>;
}

/** One island, as the gesture needs it: its model, its scene group and a few cached measurements. */
interface IslandRuntime {
	model: DragIslandModel;
	group: Object3D;
	/** island-local bounds of everything in the group */
	localBounds: Box3;
	/** world AABB grown by the capture margin, for candidate selection */
	captureBounds: Box3;
	/** +1 when the front panel sits at local +z, −1 at local −z */
	outwardSign: 1 | -1;
	/** local z of the front face */
	frontZ: number;
	/** local y of the countertop */
	topY: number;
	/** local y of the island's base (the floor) */
	floorY: number;
	meshesByGroup: Map<string, Array<Object3D>>;
}

interface MeshBase {
	mesh: Object3D;
	/** island-local position before anything moved */
	localPosition: Vector3;
	localQuaternion: Quaternion;
	scale: Vector3;
}

interface ActiveDrag {
	pointerId: number;
	pointerType: string;
	model: DragModel;
	runtimes: Map<string, IslandRuntime>;
	source: IslandRuntime;
	group: DragGroupModel;
	dragged: Array<MeshBase>;
	/** survivors on every island: their resting local positions */
	survivors: Map<string, MeshBase>;
	layer: Group;
	grabOffsetX: number;
	liftUp: number;
	tint: EmissiveTintBackup | null;
	targetTint: { groupId: string; backup: EmissiveTintBackup } | null;
	candidate: IslandRuntime;
	hypotheticalX: number;
	swapTargetId: string | null;
	/** planned x of every column on the candidate (and source) panel, from the last move */
	plan: { candidate: Array<{ id: string; x: number }>; source: Array<{ id: string; x: number }> | null };
	refusedReason: ApplianceDragRefusedReason | null;
	willResize: ApplianceDragState['willResize'];
	state: ApplianceDragState;
	previewedIslands: Set<string>;
	/** planned x delta per column, per island, from the last preview (what the cutouts follow) */
	previewDeltas: Map<string, Map<string, number>>;
	/** signature of the cutout adjustment last requested per island */
	cutoutSignatures: Map<string, string>;
	cutoutTimer: ReturnType<typeof setTimeout> | null;
	/** when the current debounce window opened, for the max-wait */
	cutoutWaitSince: number | null;
	moved: boolean;
	/** the floor label in front of the column, created on first use */
	indicator: DropIndicator | null;
	/** world position of the column's centre at the panel face, as last shown */
	lastCentre: Vector3;
	/** releasing now would only put the column back */
	putBack: boolean;
	/** the drop the assessment was last scheduled for (null: nothing to assess) */
	assessSignature: string | null;
	assessTimer: ReturnType<typeof setTimeout> | null;
	/** while a hop to another island is under way: when it ends (performance.now() base) */
	hopUntil: number;
	/** fine-tune mode (options.fineTune, same gap, order unchanged): where the column would be dropped */
	fineTuneX: number | null;
	fineTuneRoom: FineTuneRoom | null;
	/** the spacing lines on the floor while fine-tuning, created on first use */
	dimensions: DimensionOverlay | null;
}

/**
 * Press-and-hold an appliance, drag it along or across islands, drop it into a
 * new slot — or onto another appliance to trade places.
 *
 * The manager owns pointer handling, the drag planes, the mesh-only preview
 * (survivors slide to where the library planner says they would go) and the
 * visuals. The blueprint is never written while the finger moves: the
 * controller-side `ApplianceDragProvider` supplies the layout model on lift
 * and commits ONE change on release. Modelled on RotationGizmoManager.
 */
export class ApplianceDragManager {
	#config: ApplianceDragManagerConfig;
	#provider: ApplianceDragProvider | null = null;
	#enabled = false;
	#options: ApplianceDragOptions = {};

	#armed: ArmedPress | null = null;
	#lastBail: string | null = null;
	#drag: ActiveDrag | null = null;
	#finishing = false;

	#raycaster = new Raycaster();
	#pointer = new Vector2();

	#handlePointerDown: (e: PointerEvent) => void;
	#handlePointerMove: (e: PointerEvent) => void;
	#handlePointerUp: (e: PointerEvent) => void;
	#handlePointerCancel: (e: PointerEvent) => void;
	#handleContextMenu: (e: MouseEvent) => void;
	#handleKeyDown: (e: KeyboardEvent) => void;

	constructor (config: ApplianceDragManagerConfig) {
		this.#config = config;

		this.#handlePointerDown = (e) => this.#onPointerDown(e);
		this.#handlePointerMove = (e) => this.#onPointerMove(e);
		this.#handlePointerUp = (e) => this.#onPointerUp(e);
		this.#handlePointerCancel = (e) => this.#onPointerCancel(e);
		this.#handleContextMenu = (e) => {
			if (this.#armed || this.#drag) e.preventDefault();
		};
		this.#handleKeyDown = (e) => {
			if (e.key === 'Escape' && this.#drag) void this.cancel();
		};

		const { domElement } = config;
		domElement.addEventListener('pointerdown', this.#handlePointerDown);
		window.addEventListener('pointermove', this.#handlePointerMove);
		window.addEventListener('pointerup', this.#handlePointerUp);
		window.addEventListener('pointercancel', this.#handlePointerCancel);
		domElement.addEventListener('contextmenu', this.#handleContextMenu, true);
		window.addEventListener('keydown', this.#handleKeyDown);
	}

	// ── Public API ──────────────────────────────────────────────────────────

	get isActive (): boolean {
		return this.#drag !== null;
	}

	get enabled (): boolean {
		return this.#enabled;
	}

	/** Why the last press did not lift a column — read by the dev bridge when a gesture does nothing. */
	get lastBail (): string | null { return this.#lastBail; }

	#bail (reason: string): void { this.#lastBail = reason; }

	setProvider (provider: ApplianceDragProvider | null): void {
		this.#provider = provider;
	}

	setEnabled (enabled: boolean, options?: ApplianceDragOptions): void {
		this.#enabled = enabled;
		if (options) this.#options = { ...this.#options, ...options };
		if (!enabled) {
			this.#disarm();
			if (this.#drag) void this.cancel();
		}
	}

	/** Snap everything back and end the gesture (Escape, a second finger, a scene rebuild). */
	async cancel (): Promise<void> {
		const drag = this.#drag;
		if (!drag || this.#finishing) return;
		this.#finishing = true;
		try {
			this.#setState({ phase: 'cancelled', mode: null, targetIslandId: null, targetApplianceId: null, willResize: null, refusedReason: null });
			await this.#snapBack(drag);
		} finally {
			this.#teardown(drag);
		}
	}

	dispose (): void {
		this.#disarm();
		if (this.#drag) this.#teardown(this.#drag);
		const { domElement } = this.#config;
		domElement.removeEventListener('pointerdown', this.#handlePointerDown);
		window.removeEventListener('pointermove', this.#handlePointerMove);
		window.removeEventListener('pointerup', this.#handlePointerUp);
		window.removeEventListener('pointercancel', this.#handlePointerCancel);
		domElement.removeEventListener('contextmenu', this.#handleContextMenu, true);
		window.removeEventListener('keydown', this.#handleKeyDown);
	}

	// ── Pointer handling ─────────────────────────────────────────────────────

	#onPointerDown (event: PointerEvent): void {
		this.#lastBail = null;
		if (!this.#enabled || !this.#provider) return void this.#bail(this.#enabled ? 'no provider' : 'disabled');

		// a second finger while dragging cancels; while armed it just disarms
		if (this.#drag) {
			if (event.pointerId !== this.#drag.pointerId) void this.cancel();
			return;
		}
		if (this.#armed) {
			this.#disarm();
			return;
		}
		if (event.button !== 0 && event.pointerType === 'mouse') return void this.#bail('not the primary button');
		if (this.#config.isBusy?.()) return void this.#bail('scene is building');

		const hit = this.#findApplianceHit(event.clientX, event.clientY);
		if (!hit) return void this.#bail('press did not land on an appliance');

		const holdMs = event.pointerType === 'mouse'
			? (this.#options.holdMs?.mouse ?? APPLIANCE_DRAG.holdMs.mouse)
			: (this.#options.holdMs?.touch ?? APPLIANCE_DRAG.holdMs.touch);

		this.#armed = {
			pointerId: event.pointerId,
			pointerType: event.pointerType,
			startX: event.clientX,
			startY: event.clientY,
			hit,
			timer: setTimeout(() => this.#lift(), holdMs)
		};
	}

	#onPointerMove (event: PointerEvent): void {
		const armed = this.#armed;
		if (armed && event.pointerId === armed.pointerId) {
			const cancelPx = armed.pointerType === 'mouse' ? APPLIANCE_DRAG.cancelPx.mouse : APPLIANCE_DRAG.cancelPx.touch;
			const dx = event.clientX - armed.startX;
			const dy = event.clientY - armed.startY;
			if (Math.sqrt((dx * dx) + (dy * dy)) > cancelPx) this.#disarm();
			return;
		}

		const drag = this.#drag;
		if (!drag || event.pointerId !== drag.pointerId || this.#finishing) return;
		if (this.#config.isBusy?.()) {
			void this.cancel();
			return;
		}
		event.preventDefault();
		this.#updateDrag(event.clientX, event.clientY);
	}

	#onPointerUp (event: PointerEvent): void {
		if (this.#armed && event.pointerId === this.#armed.pointerId) {
			this.#disarm();
			return;
		}
		const drag = this.#drag;
		if (!drag || event.pointerId !== drag.pointerId || this.#finishing) return;
		void this.#drop(drag);
	}

	#onPointerCancel (event: PointerEvent): void {
		if (this.#armed && event.pointerId === this.#armed.pointerId) {
			this.#disarm();
			return;
		}
		if (this.#drag && event.pointerId === this.#drag.pointerId) void this.cancel();
	}

	#disarm (): void {
		if (!this.#armed) return;
		clearTimeout(this.#armed.timer);
		this.#armed = null;
	}

	// ── Lift ─────────────────────────────────────────────────────────────────

	#lift (): void {
		const armed = this.#armed;
		this.#armed = null;
		if (!armed || !this.#enabled || !this.#provider || this.#drag) return void this.#bail('hold cancelled before lift');
		if (this.#config.isBusy?.()) return void this.#bail('scene started building during the hold');

		const model = this.#provider.getModel();
		if (!model) return void this.#bail('provider returned no layout model');

		const islandModel = model.islands.find((i) => i.islandId === armed.hit.islandId);
		const group = islandModel?.groups.find((g) => g.itemIds.includes(armed.hit.applianceId));
		if (!islandModel || !group || !group.draggable) return void this.#bail(!islandModel ? 'island not in the layout model' : !group ? 'column not in the layout model' : 'column is not draggable');

		const objectHolder = this.#config.getObjectHolder();
		if (!objectHolder) return void this.#bail('no object holder');
		objectHolder.updateMatrixWorld(true);

		const runtimes = new Map<string, IslandRuntime>();
		for (const island of model.islands) {
			const islandGroup = island.islandId === islandModel.islandId
				? armed.hit.islandGroup
				: objectHolder.children.find((child) => child.userData?.type === 'island' && child.userData?.id === island.islandId) ?? null;
			if (!islandGroup) continue;
			runtimes.set(island.islandId, this.#buildRuntime(island, islandGroup));
		}
		const source = runtimes.get(islandModel.islandId);
		if (!source) return;

		const dragged = source.meshesByGroup.get(group.groupId) ?? [];
		if (dragged.length === 0) return;

		// the pointer's x on the panel at the moment of the lift decides the grab offset
		const grabLocal = this.#pointerToLocal(armed.startX, armed.startY, source);
		const grabOffsetX = grabLocal ? grabLocal.x - group.x : 0;

		const layer = new Group();
		layer.name = 'appliance-drag-layer';
		layer.userData = { type: 'appliance-drag-layer' };
		objectHolder.add(layer);

		const survivors = new Map<string, MeshBase>();
		for (const runtime of runtimes.values()) {
			for (const [groupId, meshes] of runtime.meshesByGroup) {
				if (runtime === source && groupId === group.groupId) continue;
				for (const mesh of meshes) survivors.set(mesh.uuid, this.#baseOf(mesh));
			}
		}

		const draggedBases = dragged.map((mesh) => this.#baseOf(mesh));
		for (const mesh of dragged) layer.attach(mesh);

		const topPlaced = dragged.some((mesh) => String(mesh.userData?.placement ?? mesh.userData?.subtype ?? '').toLowerCase().includes('top'));

		const drag: ActiveDrag = {
			pointerId: armed.pointerId,
			pointerType: armed.pointerType,
			model,
			runtimes,
			source,
			group,
			dragged: draggedBases,
			survivors,
			layer,
			grabOffsetX,
			liftUp: topPlaced ? APPLIANCE_DRAG.liftUpIn : 0,
			tint: null,
			targetTint: null,
			candidate: source,
			hypotheticalX: group.x,
			swapTargetId: null,
			plan: { candidate: islandModel.groups.map((g) => ({ id: g.groupId, x: g.x })), source: null },
			refusedReason: null,
			willResize: null,
			state: {
				phase: 'lifted',
				// the column's main appliance, not whichever part (trim kit, door) sat under the finger
				applianceId: group.anchorItemId || armed.hit.applianceId,
				groupId: group.groupId,
				sourceIslandId: islandModel.islandId,
				targetIslandId: islandModel.islandId,
				mode: 'insert',
				targetApplianceId: null,
				willResize: null,
				refusedReason: null
			},
			previewedIslands: new Set(),
			previewDeltas: new Map(),
			cutoutSignatures: new Map(),
			cutoutTimer: null,
			cutoutWaitSince: null,
			moved: false,
			indicator: null,
			lastCentre: source.group.localToWorld(new Vector3(group.x, 0, source.frontZ)),
			putBack: true,
			assessSignature: null,
			assessTimer: null,
			hopUntil: 0,
			fineTuneX: null,
			fineTuneRoom: null,
			dimensions: null
		};
		this.#drag = drag;

		this.#config.suppressClick?.();
		this.#config.onInteractionStart?.();
		this.#config.domElement.style.touchAction = 'none';
		this.#config.domElement.style.cursor = 'grabbing';
		try {
			this.#config.domElement.setPointerCapture(armed.pointerId);
		} catch {
			// capture is best effort; window-level listeners still see the gesture
		}
		this.#vibrate();

		drag.tint = { entries: [] };
		for (const { mesh } of draggedBases) {
			const backup = applyEmissiveTint(mesh, APPLIANCE_DRAG.tint.lifted.color, APPLIANCE_DRAG.tint.lifted.intensity);
			drag.tint.entries.push(...backup.entries);
			mesh.scale.multiplyScalar(APPLIANCE_DRAG.liftScale);
		}

		// the spotlight lands on the lifted column and follows it from here on
		this.#config.highlightObject?.(drag.state.applianceId);

		// the countertop closes over the lifted column's slot until the drop
		this.#previewCutoutsNow(drag);

		// pop-out: animate from the resting pose to the lifted pose at the current (un-moved) x
		this.#placeDragged(drag, source, group.x, APPLIANCE_DRAG.liftMs);
		this.#emitState(drag);
		this.#config.requestRender();
	}

	// ── Move ─────────────────────────────────────────────────────────────────

	#updateDrag (clientX: number, clientY: number): void {
		const drag = this.#drag;
		if (!drag || !this.#provider) return;
		drag.moved = true;

		const candidate = this.#pickCandidate(drag, clientX, clientY);
		const local = this.#pointerToLocal(clientX, clientY, candidate);
		if (!local) return;
		const switched = candidate !== drag.candidate;
		if (switched) {
			// the column stays where it is on screen across the switch: re-measure the grab offset on the
			// new panel from the column's current centre, so it pivots from there instead of leaping to
			// wherever the pointer's x lands on that panel
			drag.grabOffsetX = local.x - candidate.group.worldToLocal(drag.lastCentre.clone()).x;
		}

		// the column stays inside the panel's usable run: a connected island's depth eats the end of an
		// L-shape's panel, and the planner never places anything there, so the lifted column must not go there either
		const range = usableRangeFor(candidate.model, drag.group.width);
		const centreX = local.x - drag.grabOffsetX;
		const hypotheticalX = Math.min(range.max, Math.max(range.min, centreX));
		const sameIsland = candidate === drag.source;
		const candidateGroups = candidate.model.groups.map(toDecision);
		const sourceGroups = drag.source.model.groups.map(toDecision);

		// the swap decision follows the pointer's centre line even past the usable run: on a full island the
		// shown column stops at the panel's end while still over the last column, and only the unclamped
		// centre line can leave that column and insert at the end
		const swapTargetId = decideSwapTargetCfn({
			hypotheticalX: centreX,
			draggedId: drag.group.groupId,
			groups: candidateGroups,
			previousSwapTargetId: drag.candidate === candidate ? drag.swapTargetId : null,
			enterFraction: APPLIANCE_DRAG.swapEnterFraction,
			exitFraction: APPLIANCE_DRAG.swapExitFraction
		});

		const request = {
			mode: (swapTargetId ? 'swap' : 'insert') as 'swap' | 'insert',
			sameIsland,
			hypotheticalX,
			dragged: { id: drag.group.groupId, width: drag.group.width, originalX: drag.group.x },
			candidateGroups,
			sourceGroups,
			swapTargetId
		};

		// growth / refusal — only a cross-island drop can change an island's width
		let refusedReason: ApplianceDragRefusedReason | null = null;
		let willResize: ApplianceDragState['willResize'] = null;
		if (!sameIsland) {
			const widths = hypotheticalWidthsCfn(request);
			const outcome = this.#resolveGrowth(candidate, widths.candidate);
			if (outcome.refused) refusedReason = outcome.refused;
			else willResize = outcome.resize;
			if (!refusedReason && swapTargetId && widths.source) {
				const back = this.#resolveGrowth(drag.source, widths.source);
				if (back.refused) refusedReason = back.refused;
				else if (back.resize && !willResize) willResize = back.resize;
			}
		}

		const previousCandidate = drag.candidate;
		drag.candidate = candidate;
		drag.hypotheticalX = hypotheticalX;
		const swapChanged = drag.swapTargetId !== swapTargetId;
		drag.swapTargetId = swapTargetId;
		drag.refusedReason = refusedReason;
		drag.willResize = willResize;

		// survivors on islands that are no longer involved go home
		if (previousCandidate !== candidate && previousCandidate !== drag.source) {
			this.#restoreIslandSurvivors(drag, previousCandidate);
		}

		let fineTuneRoom: FineTuneRoom | null = null;
		let fineTuneX: number | null = null;
		if (refusedReason) {
			if (candidate !== drag.source) this.#restoreIslandSurvivors(drag, candidate);
			this.#previewSourceWithout(drag, false);
			drag.plan = { candidate: candidate.model.groups.map((g) => ({ id: g.groupId, x: g.x })), source: null };
		} else {
			const inputs = buildPreviewPlanInputsCfn(request);
			const candidatePlan = this.#provider.planLayout({ panelWidth: candidate.model.panelWidth, overlap: candidate.model.overlap, groups: inputs.candidate });
			const sourcePlan = inputs.source
				? this.#provider.planLayout({ panelWidth: drag.source.model.panelWidth, overlap: drag.source.model.overlap, groups: inputs.source })
				: null;
			drag.plan = { candidate: candidatePlan, source: sourcePlan };
			// fine-tune: between the same two neighbours the column goes exactly where it is released;
			// nothing re-spaces, and the room on each side is what the floor lines show
			fineTuneRoom = this.#options.fineTune && sameIsland && !swapTargetId && sameOrder(candidatePlan, drag.source.model.groups)
				? fineTuneRoomCfn({ groups: candidateGroups, draggedId: drag.group.groupId, panelWidth: candidate.model.panelWidth, overlap: candidate.model.overlap })
				: null;
			if (fineTuneRoom) {
				fineTuneX = Math.round(Math.min(fineTuneRoom.maxX, Math.max(fineTuneRoom.minX, hypotheticalX)) * 16) / 16;
				this.#restoreIslandSurvivors(drag, candidate);
			} else if (swapTargetId) {
				// over a target nothing shifts: every column rests where it is and the target only glows;
				// the exchange is previewed by the plan alone and happens at the drop
				this.#restoreIslandSurvivors(drag, candidate);
				if (!sameIsland) this.#restoreIslandSurvivors(drag, drag.source);
			} else {
				this.#applySurvivorPlan(drag, candidate, candidatePlan);
				if (sourcePlan) this.#applySurvivorPlan(drag, drag.source, sourcePlan);
			}
		}

		// a change of island glides the column around the corner instead of snapping it there; while
		// that glide runs, every further move re-targets it for the time it has left, so a finger that
		// keeps travelling never cuts it short into a jump
		drag.fineTuneX = fineTuneX;
		drag.fineTuneRoom = fineTuneRoom;
		const now = performance.now();
		if (switched) drag.hopUntil = now + APPLIANCE_DRAG.hopMs;
		// linear: a re-targeted ease-in-out would restart its slow start on every move and leave the
		// whole distance for the last frame
		this.#placeDragged(drag, candidate, fineTuneX ?? hypotheticalX, Math.max(0, drag.hopUntil - now), linear);
		this.#applyTargetTint(drag, swapTargetId && !refusedReason ? swapTargetId : null, candidate);
		if (swapChanged && swapTargetId) this.#vibrate();

		this.#setState({
			phase: 'dragging',
			targetIslandId: candidate.model.islandId,
			mode: refusedReason ? 'refused' : (swapTargetId ? 'swap' : (fineTuneX !== null ? 'fine-tune' : 'insert')),
			targetApplianceId: swapTargetId ? candidate.model.groups.find((g) => g.groupId === swapTargetId)?.anchorItemId ?? null : null,
			willResize,
			refusedReason,
			spacing: fineTuneRoom && fineTuneX !== null ? this.#spacingFor(drag, candidate, fineTuneRoom, fineTuneX) : null
		});
		// the label says nothing when releasing would only put the column back
		const putBack = fineTuneX !== null
			? Math.abs(fineTuneX - drag.group.x) < 0.0625
			: sameIsland && !swapTargetId && !refusedReason && sameOrder(drag.plan.candidate, drag.source.model.groups);
		drag.putBack = putBack;
		this.#updateIndicator(drag, candidate, fineTuneX ?? hypotheticalX, putBack);
		this.#updateDimensions(drag, candidate);
		this.#scheduleAssessment(drag, candidate);
		this.#config.requestRender();
	}

	// ── Fine-tune ────────────────────────────────────────────────────────────

	#spacingFor (drag: ActiveDrag, island: IslandRuntime, room: FineTuneRoom, x: number): ApplianceDragState['spacing'] {
		const half = drag.group.width / 2;
		const anchorOf = (g: DecisionGroup | null): string | null => (g ? island.model.groups.find((m) => m.groupId === g.id)?.anchorItemId ?? null : null);
		const sixteenth = (value: number): number => Math.round(value * 16) / 16;
		return {
			left: { applianceId: anchorOf(room.left), inches: sixteenth((x - half) - room.leftEdge) },
			right: { applianceId: anchorOf(room.right), inches: sixteenth(room.rightEdge - (x + half)) }
		};
	}

	/** The spacing lines on the floor, for the room on each side of the column as it sits now. */
	#updateDimensions (drag: ActiveDrag, island: IslandRuntime): void {
		const room = drag.fineTuneRoom;
		const x = drag.fineTuneX;
		if (!room || x === null) {
			this.#hideDimensions(drag);
			return;
		}
		if (!drag.dimensions) drag.dimensions = createDimensionOverlayCfn(APPLIANCE_DRAG.fineTune.labelWidthIn, APPLIANCE_DRAG.fineTune.labelHeightIn);
		const overlay = drag.dimensions;
		if (!overlay) return;
		const half = drag.group.width / 2;
		placeDimensionOverlayCfn(overlay, island, APPLIANCE_DRAG.fineTune.dimensionOffsetIn, APPLIANCE_DRAG.indicator.floorLiftIn, {
			left: { from: room.leftEdge, to: x - half },
			right: { from: x + half, to: room.rightEdge }
		});
	}

	#hideDimensions (drag: ActiveDrag): void {
		if (drag.dimensions) drag.dimensions.group.visible = false;
	}

	// ── Drop assessment ──────────────────────────────────────────────────────

	/**
	 * Ask the provider what validations the drop would leave behind, once the slot has stopped
	 * changing for a moment. The answer (a cloned kitchen, ~100 ms) lands in the state and the
	 * floor label; a changed slot clears it until the next answer.
	 */
	#scheduleAssessment (drag: ActiveDrag, candidate: IslandRuntime): void {
		const provider = this.#provider;
		const planned = drag.plan.candidate.find((p) => p.id === drag.group.groupId) ?? null;
		const wanted = Boolean(this.#options.assessDrops && provider?.assessDrop) && !drag.putBack && !drag.refusedReason && planned !== null;
		const request: ApplianceDropRequest | null = wanted && planned
			? {
				groupId: drag.group.groupId,
				applianceId: drag.state.applianceId,
				sourceIslandId: drag.source.model.islandId,
				targetIslandId: candidate.model.islandId,
				mode: drag.fineTuneX !== null ? 'fine-tune' : (drag.swapTargetId ? 'swap' : 'insert'),
				x: Math.round((drag.fineTuneX ?? planned.x) * 4) / 4,
				targetGroupId: drag.swapTargetId ?? undefined
			}
			: null;
		const signature = request ? JSON.stringify(request) : null;
		if (signature === drag.assessSignature) return;
		drag.assessSignature = signature;
		if (drag.assessTimer !== null) {
			clearTimeout(drag.assessTimer);
			drag.assessTimer = null;
		}
		if (drag.state.issues !== undefined || drag.state.resolvedIssues !== undefined) this.#setState({ issues: undefined, resolvedIssues: undefined });
		if (!request || !provider?.assessDrop) return;
		const assess = provider.assessDrop;
		drag.assessTimer = setTimeout(() => {
			drag.assessTimer = null;
			if (this.#drag !== drag || this.#finishing || drag.assessSignature !== signature) return;
			let result: ApplianceDropAssessment | null = null;
			try {
				result = assess(request);
			} catch (error) {
				console.warn('[ApplianceDragManager] assessDrop failed', error);
			}
			if (this.#drag !== drag || drag.assessSignature !== signature) return;
			this.#setState({ issues: result?.issues ?? [], resolvedIssues: result?.resolvedCount ?? 0 });
			this.#updateIndicator(drag, drag.candidate, drag.hypotheticalX, drag.putBack);
			this.#config.requestRender();
		}, APPLIANCE_DRAG.assessDebounceMs);
	}

	// ── Floor label ──────────────────────────────────────────────────────────

	#describeDrop (state: ApplianceDragState): ReturnType<typeof defaultDropIndicatorCfn> {
		try {
			const custom = this.#options.describeDrop?.(state);
			if (custom !== undefined) return custom;
		} catch (error) {
			console.warn('[ApplianceDragManager] describeDrop failed, using the generic label', error);
		}
		return defaultDropIndicatorCfn(state);
	}

	#updateIndicator (drag: ActiveDrag, island: IslandRuntime, x: number, putBack: boolean): void {
		const content = putBack ? null : this.#describeDrop(drag.state);
		if (!content) {
			this.#hideIndicator(drag);
			return;
		}
		if (!drag.indicator) drag.indicator = createDropIndicatorCfn(APPLIANCE_DRAG.indicator.widthIn, APPLIANCE_DRAG.indicator.heightIn);
		const indicator = drag.indicator;
		if (!indicator) return;
		const shown = setDropIndicatorContentCfn(indicator, content);
		placeDropIndicatorCfn(indicator, island, x, APPLIANCE_DRAG.indicator.offsetIn, APPLIANCE_DRAG.indicator.floorLiftIn);
		if (shown) {
			const { material } = indicator.mesh;
			material.opacity = 0;
			this.#config.animationManager.start({
				id: 'drag-indicator-fade',
				duration: APPLIANCE_DRAG.indicator.fadeMs,
				onUpdate: (_, eased) => {
					material.opacity = eased;
					this.#config.requestRender();
				}
			});
		}
	}

	#hideIndicator (drag: ActiveDrag): void {
		if (!drag.indicator) return;
		this.#config.animationManager.stop('drag-indicator-fade');
		setDropIndicatorContentCfn(drag.indicator, null);
	}

	#resolveGrowth (island: IslandRuntime, widths: Array<number>): { refused: ApplianceDragRefusedReason | null; resize: ApplianceDragState['willResize'] } {
		const prediction = this.#provider!.predictGrowth(island.model, widths);
		if (prediction.fits) return { refused: null, resize: null };
		if (!island.model.growable) return { refused: 'locked-island-no-room', resize: null };
		const max = this.#options.maxIslandWidthIn;
		if (max !== undefined && prediction.finalWidth > max) return { refused: 'exceeds-max-width', resize: null };
		return { refused: null, resize: { islandId: island.model.islandId, fromWidthIn: island.model.widthIn, toWidthIn: prediction.finalWidth } };
	}

	// ── Drop ─────────────────────────────────────────────────────────────────

	async #drop (drag: ActiveDrag): Promise<void> {
		if (this.#finishing || !this.#provider) return;
		this.#finishing = true;
		if (drag.assessTimer !== null) {
			clearTimeout(drag.assessTimer);
			drag.assessTimer = null;
		}
		this.#hideIndicator(drag);

		const candidate = drag.candidate;
		const sameIsland = candidate === drag.source;
		const swapTargetId = drag.swapTargetId;
		const fineTuneX = drag.fineTuneX;
		const planned = drag.plan.candidate.find((p) => p.id === drag.group.groupId);
		const orderUnchanged = sameIsland && !swapTargetId && sameOrder(drag.plan.candidate, drag.source.model.groups);
		// in fine-tune mode a release inside the same gap is a change unless it lands where it started
		const putBack = fineTuneX !== null ? Math.abs(fineTuneX - drag.group.x) < 0.0625 : orderUnchanged;
		const noop = !drag.moved || drag.refusedReason !== null || !planned || putBack;
		this.#hideDimensions(drag);

		if (noop) {
			this.#setState({ phase: 'cancelled', mode: null, targetIslandId: null, targetApplianceId: null, willResize: null, refusedReason: drag.refusedReason });
			await this.#snapBack(drag);
			this.#teardown(drag);
			return;
		}

		this.#setState({ phase: 'dropping' });

		// the commit's rebuild regenerates every hole from the domain; a late preview job would undo that,
		// so drop anything queued and let a running CSG pass finish before the rebuild starts
		if (drag.cutoutTimer !== null) {
			clearTimeout(drag.cutoutTimer);
			drag.cutoutTimer = null;
		}
		await this.#config.cancelCutoutPreviews?.();

		// settle into the planned slot (or exactly where it was fine-tuned to) and hand the meshes to
		// the island that will own them
		const settleX = fineTuneX ?? planned.x;
		this.#restoreLiftLook(drag);
		await this.#placeDraggedAsync(drag, candidate, settleX, APPLIANCE_DRAG.settleMs, false);
		for (const { mesh } of drag.dragged) candidate.group.attach(mesh);

		const request: ApplianceDropRequest = {
			groupId: drag.group.groupId,
			applianceId: drag.state.applianceId,
			sourceIslandId: drag.source.model.islandId,
			targetIslandId: candidate.model.islandId,
			mode: fineTuneX !== null ? 'fine-tune' : (swapTargetId ? 'swap' : 'insert'),
			x: settleX,
			targetGroupId: swapTargetId ?? undefined
		};

		try {
			const result = await this.#provider.commitDrop(request);
			this.#config.emitEvent('appliance-drag:drop', result);
			this.#setState({ phase: 'idle', mode: null, targetIslandId: null, targetApplianceId: null, willResize: null, refusedReason: null });
			this.#teardown(drag, false);
		} catch (error) {
			console.warn('[ApplianceDragManager] drop refused at commit, snapping back', error);
			for (const { mesh } of drag.dragged) drag.layer.attach(mesh);
			this.#setState({ phase: 'cancelled', mode: null, targetIslandId: null, targetApplianceId: null, willResize: null, refusedReason: null });
			await this.#snapBack(drag);
			this.#teardown(drag);
		}
	}

	// ── Geometry ─────────────────────────────────────────────────────────────

	#findApplianceHit (clientX: number, clientY: number): ApplianceHit | null {
		const objectHolder = this.#config.getObjectHolder();
		if (!objectHolder) return null;
		this.#setPointer(clientX, clientY);
		this.#raycaster.setFromCamera(this.#pointer, this.#config.camera);
		const intersects = this.#raycaster.intersectObjects(objectHolder.children, true);
		for (const intersect of intersects) {
			let node: Object3D | null = intersect.object;
			let appliance: Object3D | null = null;
			let island: Object3D | null = null;
			let blocked = false;
			while (node) {
				const type = node.userData?.type as string | undefined;
				if (type && NON_HIT_TYPES.has(type)) {
					blocked = true;
					break;
				}
				if (!appliance && type === 'appliance' && node.userData?.id) appliance = node;
				if (appliance && type === 'island') {
					island = node;
					break;
				}
				node = node.parent;
			}
			if (blocked) continue;
			if (appliance && island) {
				return { applianceId: String(appliance.userData.id), object: appliance, islandGroup: island, islandId: String(island.userData.id) };
			}
			// the nearest hit was something else (a panel, the countertop): no lift
			if (!appliance) return null;
		}
		return null;
	}

	#setPointer (clientX: number, clientY: number): void {
		const rect = this.#config.domElement.getBoundingClientRect();
		this.#pointer.x = (((clientX - rect.left) / rect.width) * 2) - 1;
		this.#pointer.y = (-((clientY - rect.top) / rect.height) * 2) + 1;
	}

	#buildRuntime (model: DragIslandModel, group: Object3D): IslandRuntime {
		const inverse = new Matrix4().copy(group.matrixWorld).invert();
		const localBounds = new Box3();
		const corner = new Vector3();
		group.traverse((child) => {
			const mesh = child as Object3D & { geometry?: { boundingBox: Box3 | null; computeBoundingBox: () => void } };
			if (!mesh.geometry) return;
			if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
			const box = mesh.geometry.boundingBox;
			if (!box) return;
			const toLocal = new Matrix4().multiplyMatrices(inverse, mesh.matrixWorld);
			for (let i = 0; i < 8; i++) {
				corner.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).applyMatrix4(toLocal);
				localBounds.expandByPoint(corner);
			}
		});
		if (localBounds.isEmpty()) localBounds.set(new Vector3(-model.widthIn / 2, 0, -14), new Vector3(model.widthIn / 2, 36, 14));

		const meshesByGroup = new Map<string, Array<Object3D>>();
		const itemToGroup = new Map<string, string>();
		for (const g of model.groups) for (const itemId of g.itemIds) itemToGroup.set(itemId, g.groupId);
		group.traverse((child) => {
			if (child.userData?.type !== 'appliance' || !child.userData?.id) return;
			const groupId = itemToGroup.get(String(child.userData.id));
			if (!groupId) return;
			const list = meshesByGroup.get(groupId) ?? [];
			list.push(child);
			meshesByGroup.set(groupId, list);
		});

		// the front panel is the side the appliances sit on
		const center = localBounds.getCenter(new Vector3());
		let zSum = 0;
		let count = 0;
		for (const meshes of meshesByGroup.values()) {
			for (const mesh of meshes) {
				zSum += mesh.position.z - center.z;
				count++;
			}
		}
		const outwardSign: 1 | -1 = count > 0 ? (zSum >= 0 ? 1 : -1) : 1;
		const frontZ = outwardSign === 1 ? localBounds.max.z : localBounds.min.z;

		const captureBounds = new Box3().setFromObject(group).expandByScalar(APPLIANCE_DRAG.islandCaptureMarginIn);

		// the countertop, not the bounding box top: a grill hood or a tall fridge would lift the plane above a low camera
		const topY = model.heightIn > 0 ? Math.min(model.heightIn, localBounds.max.y) : localBounds.max.y;

		return { model, group, localBounds, captureBounds, outwardSign, frontZ, topY, floorY: localBounds.min.y, meshesByGroup };
	}

	#pickCandidate (drag: ActiveDrag, clientX: number, clientY: number): IslandRuntime {
		this.#setPointer(clientX, clientY);
		this.#raycaster.setFromCamera(this.#pointer, this.#config.camera);
		const ray = this.#raycaster.ray;
		const hit = new Vector3();

		// stay with the current island while the ray still crosses its box AND the pointer projects
		// onto its panel's run (a margin past each end): in an L the two boxes overlap at the corner
		// and which one the ray enters first depends on the camera, so the candidate would flip with
		// every move there and the column would leap between the panels; the run check lets go once
		// the pointer has clearly travelled onto the other island
		if (ray.intersectBox(drag.candidate.captureBounds, hit)) {
			const local = this.#pointerToLocal(clientX, clientY, drag.candidate);
			const range = usableRangeFor(drag.candidate.model, drag.group.width);
			const margin = APPLIANCE_DRAG.islandCaptureMarginIn;
			if (local && local.x >= range.min - margin && local.x <= range.max + margin) return drag.candidate;
			this.#setPointer(clientX, clientY);
			this.#raycaster.setFromCamera(this.#pointer, this.#config.camera);
		}

		// otherwise the island whose (margin-grown) box the view ray enters first; a plane
		// would miss entirely from a camera lower than the plane
		let best: IslandRuntime | null = null;
		let bestDistance = Infinity;
		for (const runtime of drag.runtimes.values()) {
			if (ray.intersectBox(runtime.captureBounds, hit)) {
				const distance = hit.distanceTo(ray.origin);
				if (distance < bestDistance) {
					bestDistance = distance;
					best = runtime;
				}
			}
		}
		if (best) return best;

		// nothing under the pointer: the island the ray passes closest to
		for (const runtime of drag.runtimes.values()) {
			const distance = ray.distanceToPoint(runtime.captureBounds.getCenter(hit));
			if (distance < bestDistance) {
				bestDistance = distance;
				best = runtime;
			}
		}
		return best ?? drag.candidate;
	}

	/** Island-local point under the pointer, via the countertop plane or the panel-face plane, whichever the view crosses more squarely. */
	#pointerToLocal (clientX: number, clientY: number, island: IslandRuntime): Vector3 | null {
		this.#setPointer(clientX, clientY);
		this.#raycaster.setFromCamera(this.#pointer, this.#config.camera);
		const ray = this.#raycaster.ray;

		const up = new Vector3(0, 1, 0);
		const outward = new Vector3(0, 0, island.outwardSign).transformDirection(island.group.matrixWorld).normalize();
		const topPoint = island.group.localToWorld(new Vector3(0, island.topY, 0));
		const frontPoint = island.group.localToWorld(new Vector3(0, 0, island.frontZ));

		const kind = choosePlaneCfn(Math.abs(ray.direction.dot(up)), Math.abs(ray.direction.dot(outward)));
		const plane = kind === 'horizontal'
			? new Plane().setFromNormalAndCoplanarPoint(up, topPoint)
			: new Plane().setFromNormalAndCoplanarPoint(outward, frontPoint);

		const hit = ray.intersectPlane(plane, new Vector3());
		if (!hit) {
			const fallback = kind === 'horizontal'
				? new Plane().setFromNormalAndCoplanarPoint(outward, frontPoint)
				: new Plane().setFromNormalAndCoplanarPoint(up, topPoint);
			if (!ray.intersectPlane(fallback, hit)) return null;
		}
		return island.group.worldToLocal(hit);
	}

	#baseOf (mesh: Object3D): MeshBase {
		return { mesh, localPosition: mesh.position.clone(), localQuaternion: mesh.quaternion.clone(), scale: mesh.scale.clone() };
	}

	/**
	 * World pose of a dragged mesh shown on `island` with the column at `x`:
	 * keep each mesh's offset from the column centre, its height, and its
	 * distance from the panel face; then add the lift.
	 */
	#draggedPose (drag: ActiveDrag, island: IslandRuntime, x: number, base: MeshBase, lifted: boolean): { position: Vector3; quaternion: Quaternion } {
		const source = drag.source;
		const localX = x + (base.localPosition.x - drag.group.x);
		const localY = base.localPosition.y + (lifted ? drag.liftUp : 0);
		// distance from the panel face, measured outward on the source island and re-applied outward on the shown island
		const outwardOffset = (base.localPosition.z - source.frontZ) * source.outwardSign;
		const localZ = island.frontZ + (island.outwardSign * (outwardOffset + (lifted ? APPLIANCE_DRAG.liftOutIn : 0)));
		const position = island.group.localToWorld(new Vector3(localX, localY, localZ));
		const quaternion = island.group.getWorldQuaternion(new Quaternion()).multiply(base.localQuaternion);
		return { position, quaternion };
	}

	#placeDragged (drag: ActiveDrag, island: IslandRuntime, x: number, durationMs: number, easing?: (t: number) => number): void {
		void this.#placeDraggedAsync(drag, island, x, durationMs, true, easing);
	}

	async #placeDraggedAsync (drag: ActiveDrag, island: IslandRuntime, x: number, durationMs: number, lifted: boolean, easing?: (t: number) => number): Promise<void> {
		const { animationManager } = this.#config;
		drag.lastCentre = island.group.localToWorld(new Vector3(x, 0, island.frontZ));
		const pending: Array<Promise<void>> = [];
		for (const base of drag.dragged) {
			const { position, quaternion } = this.#draggedPose(drag, island, x, base, lifted);
			const { mesh } = base;
			if (durationMs <= 0) {
				// an in-flight lift/settle tween would keep overwriting the pose
				animationManager.stop(`drag-${mesh.uuid}`);
				mesh.position.copy(position);
				mesh.quaternion.copy(quaternion);
				continue;
			}
			// position and orientation together: a hop to another island turns the column as it travels
			const fromPosition = mesh.position.clone();
			const fromQuaternion = mesh.quaternion.clone();
			pending.push(new Promise<void>((resolve) => {
				animationManager.start({
					id: `drag-${mesh.uuid}`,
					duration: durationMs,
					easing,
					onUpdate: (_, eased) => {
						mesh.position.lerpVectors(fromPosition, position, eased);
						mesh.quaternion.slerpQuaternions(fromQuaternion, quaternion, eased);
						this.#config.followHighlight?.(drag.state.applianceId);
					},
					onComplete: () => {
						mesh.position.copy(position);
						mesh.quaternion.copy(quaternion);
						resolve();
					}
				});
			}));
		}
		this.#config.followHighlight?.(drag.state.applianceId);
		if (pending.length) await Promise.all(pending);
		this.#config.followHighlight?.(drag.state.applianceId);
		this.#config.requestRender();
	}

	#applySurvivorPlan (drag: ActiveDrag, island: IslandRuntime, plan: Array<{ id: string; x: number }>): void {
		drag.previewedIslands.add(island.model.islandId);
		const deltas = new Map<string, number>();
		for (const g of island.model.groups) {
			if (island === drag.source && g.groupId === drag.group.groupId) continue;
			const planned = plan.find((p) => p.id === g.groupId);
			const delta = planned ? planned.x - g.x : 0;
			if (delta !== 0) deltas.set(g.groupId, delta);
			this.#slideGroup(drag, island, g.groupId, delta);
		}
		const previous = drag.previewDeltas.get(island.model.islandId);
		const changed = !previous || previous.size !== deltas.size || [...deltas].some(([id, d]) => previous.get(id) !== d);
		drag.previewDeltas.set(island.model.islandId, deltas);
		if (changed) this.#scheduleCutoutPreview(drag);
	}

	#restoreIslandSurvivors (drag: ActiveDrag, island: IslandRuntime): void {
		if (!drag.previewedIslands.has(island.model.islandId)) return;
		drag.previewedIslands.delete(island.model.islandId);
		drag.previewDeltas.delete(island.model.islandId);
		for (const g of island.model.groups) {
			if (island === drag.source && g.groupId === drag.group.groupId) continue;
			this.#slideGroup(drag, island, g.groupId, 0);
		}
		this.#scheduleCutoutPreview(drag);
	}

	// ── Cutout preview ───────────────────────────────────────────────────────

	/** What each island's holes should look like right now: none for the lifted column, shifted for the columns sliding aside. */
	#cutoutAdjustFor (drag: ActiveDrag, island: IslandRuntime, lifted: boolean): { excludeApplianceIds: Array<string>; offsetsByApplianceId: Record<string, { x: number; z: number }> } {
		const offsetsByApplianceId: Record<string, { x: number; z: number }> = {};
		const deltas = drag.previewDeltas.get(island.model.islandId);
		if (deltas) {
			for (const g of island.model.groups) {
				const delta = deltas.get(g.groupId);
				if (!delta) continue;
				for (const itemId of g.itemIds) offsetsByApplianceId[itemId] = { x: delta, z: 0 };
			}
		}
		return { excludeApplianceIds: lifted && island === drag.source ? [...drag.group.itemIds] : [], offsetsByApplianceId };
	}

	#scheduleCutoutPreview (drag: ActiveDrag): void {
		if (!this.#config.previewCutouts) return;
		const now = Date.now();
		if (drag.cutoutWaitSince === null) drag.cutoutWaitSince = now;
		const waited = now - drag.cutoutWaitSince;
		const delay = Math.max(0, Math.min(APPLIANCE_DRAG.cutoutPreviewDebounceMs, APPLIANCE_DRAG.cutoutPreviewMaxWaitMs - waited));
		if (drag.cutoutTimer !== null) clearTimeout(drag.cutoutTimer);
		drag.cutoutTimer = setTimeout(() => {
			drag.cutoutTimer = null;
			drag.cutoutWaitSince = null;
			if (this.#drag === drag) this.#previewCutoutsNow(drag);
		}, delay);
	}

	/** Push the current adjustment to every island whose holes differ from what was last requested. */
	#previewCutoutsNow (drag: ActiveDrag, lifted = true): void {
		const preview = this.#config.previewCutouts;
		if (!preview) return;
		for (const runtime of drag.runtimes.values()) {
			const adjust = this.#cutoutAdjustFor(drag, runtime, lifted);
			const signature = JSON.stringify(adjust);
			const islandId = runtime.model.islandId;
			const untouched = adjust.excludeApplianceIds.length === 0 && Object.keys(adjust.offsetsByApplianceId).length === 0;
			if (untouched && !drag.cutoutSignatures.has(islandId)) continue;
			if (drag.cutoutSignatures.get(islandId) === signature) continue;
			drag.cutoutSignatures.set(islandId, signature);
			void preview(islandId, adjust);
		}
	}

	/** Put the domain's holes back on every island the preview touched (cancel / snap-back). */
	#restoreCutouts (drag: ActiveDrag): void {
		if (drag.cutoutTimer !== null) {
			clearTimeout(drag.cutoutTimer);
			drag.cutoutTimer = null;
		}
		drag.previewDeltas.clear();
		this.#previewCutoutsNow(drag, false);
	}

	#previewSourceWithout (drag: ActiveDrag, _animate: boolean): void {
		if (!this.#provider) return;
		const groups = drag.source.model.groups.filter((g) => g.groupId !== drag.group.groupId).map((g) => ({ id: g.groupId, width: g.width, x: g.x, locked: g.locked, distributable: g.distributable }));
		const plan = this.#provider.planLayout({ panelWidth: drag.source.model.panelWidth, overlap: drag.source.model.overlap, groups });
		this.#applySurvivorPlan(drag, drag.source, plan);
	}

	#slideGroup (drag: ActiveDrag, island: IslandRuntime, groupId: string, deltaX: number): void {
		const meshes = island.meshesByGroup.get(groupId) ?? [];
		for (const mesh of meshes) {
			const base = drag.survivors.get(mesh.uuid);
			if (!base) continue;
			const target = base.localPosition.clone();
			target.x += deltaX;
			if (mesh.position.distanceTo(target) < 1e-4) continue;
			this.#config.animationManager.animatePosition(`drag-slide-${mesh.uuid}`, mesh.position.clone(), target, APPLIANCE_DRAG.previewMs, (p) => mesh.position.copy(p));
		}
	}

	#applyTargetTint (drag: ActiveDrag, groupId: string | null, island: IslandRuntime): void {
		if (drag.targetTint && drag.targetTint.groupId === groupId) return;
		if (drag.targetTint) {
			this.#config.animationManager.stop('drag-swap-pulse');
			restoreEmissiveTint(drag.targetTint.backup);
			drag.targetTint = null;
		}
		if (!groupId) return;
		const meshes = island.meshesByGroup.get(groupId) ?? [];
		if (meshes.length === 0) return;
		const backup: EmissiveTintBackup = { entries: [] };
		const { color, min, max, periodMs } = APPLIANCE_DRAG.tint.swapTarget;
		for (const mesh of meshes) backup.entries.push(...applyEmissiveTint(mesh, color, min).entries);
		drag.targetTint = { groupId, backup };
		const pulse = (): void => {
			if (!drag.targetTint || drag.targetTint.groupId !== groupId) return;
			this.#config.animationManager.start({
				id: 'drag-swap-pulse',
				duration: periodMs,
				easing: (t) => t,
				onUpdate: (p) => {
					setEmissiveTintIntensity(backup, min + ((max - min) * (0.5 - (0.5 * Math.cos(2 * Math.PI * p)))));
					this.#config.requestRender();
				},
				onComplete: pulse
			});
		};
		pulse();
	}

	#restoreLiftLook (drag: ActiveDrag): void {
		if (drag.tint) {
			restoreEmissiveTint(drag.tint);
			drag.tint = null;
		}
		for (const base of drag.dragged) base.mesh.scale.copy(base.scale);
		this.#applyTargetTint(drag, null, drag.candidate);
	}

	async #snapBack (drag: ActiveDrag): Promise<void> {
		this.#hideIndicator(drag);
		this.#hideDimensions(drag);
		this.#restoreLiftLook(drag);
		this.#restoreCutouts(drag);
		for (const islandId of [...drag.previewedIslands]) {
			const runtime = drag.runtimes.get(islandId);
			if (runtime) this.#restoreIslandSurvivors(drag, runtime);
		}
		await this.#placeDraggedAsync(drag, drag.source, drag.group.x, APPLIANCE_DRAG.snapBackMs, false);
		for (const base of drag.dragged) {
			drag.source.group.attach(base.mesh);
			base.mesh.position.copy(base.localPosition);
			base.mesh.quaternion.copy(base.localQuaternion);
		}
		this.#config.followHighlight?.(drag.state.applianceId);
	}

	#teardown (drag: ActiveDrag, toIdle = true): void {
		if (drag.cutoutTimer !== null) {
			clearTimeout(drag.cutoutTimer);
			drag.cutoutTimer = null;
		}
		if (drag.assessTimer !== null) {
			clearTimeout(drag.assessTimer);
			drag.assessTimer = null;
		}
		if (drag.tint) restoreEmissiveTint(drag.tint);
		if (drag.targetTint) {
			this.#config.animationManager.stop('drag-swap-pulse');
			restoreEmissiveTint(drag.targetTint.backup);
		}
		if (drag.indicator) {
			this.#config.animationManager.stop('drag-indicator-fade');
			disposeDropIndicatorCfn(drag.indicator);
			drag.indicator = null;
		}
		if (drag.dimensions) {
			disposeDimensionOverlayCfn(drag.dimensions);
			drag.dimensions = null;
		}
		drag.layer.parent?.remove(drag.layer);
		try {
			this.#config.domElement.releasePointerCapture(drag.pointerId);
		} catch {
			// not captured
		}
		this.#config.domElement.style.cursor = '';
		this.#config.onInteractionEnd?.();
		this.#drag = null;
		this.#finishing = false;
		if (toIdle) this.#setState({ phase: 'idle', mode: null, targetIslandId: null, targetApplianceId: null, willResize: null, refusedReason: null }, drag);
		this.#config.requestRender();
	}

	// ── State ────────────────────────────────────────────────────────────────

	#setState (partial: Partial<ApplianceDragState>, drag: ActiveDrag | null = this.#drag): void {
		if (!drag) return;
		const next = { ...drag.state, ...partial };
		const changed = (Object.keys(next) as Array<keyof ApplianceDragState>).some((k) => JSON.stringify(next[k]) !== JSON.stringify(drag.state[k]));
		drag.state = next;
		if (changed) this.#emitState(drag);
	}

	#emitState (drag: ActiveDrag): void {
		const snapshot = { ...drag.state };
		this.#provider?.onState?.(snapshot);
		this.#config.emitEvent('appliance-drag:state', snapshot);
	}

	#vibrate (): void {
		try {
			if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(APPLIANCE_DRAG.hapticMs);
		} catch {
			// not supported
		}
	}
}

const linear = (t: number): number => t;

const toDecision = (g: DragGroupModel): DecisionGroup => ({ id: g.groupId, x: g.x, width: g.width, locked: g.locked, distributable: g.distributable, draggable: g.draggable });

/** Where a column of `columnWidth` may have its centre on this panel: the usable run (overlap excluded), half a width in from each end. */
const usableRangeFor = (model: DragIslandModel, columnWidth: number): { min: number; max: number } => {
	const min = (-model.panelWidth / 2) + model.overlap.left + (columnWidth / 2);
	const max = (model.panelWidth / 2) - model.overlap.right - (columnWidth / 2);
	if (min > max) {
		const mid = (min + max) / 2;
		return { min: mid, max: mid };
	}
	return { min, max };
};

/** True when the planned left-to-right order equals the island's current order. */
const sameOrder = (plan: Array<{ id: string; x: number }>, groups: Array<DragGroupModel>): boolean => {
	const planned = [...plan].sort((a, b) => a.x - b.x).map((p) => p.id);
	const current = groups.filter((g) => g.distributable).sort((a, b) => a.x - b.x).map((g) => g.groupId);
	return planned.length === current.length && planned.every((id, i) => id === current[i]);
};

export { describeSlotCfn };
